import { captureEvent as captureSentryEvent } from '@sentry/capacitor'

import config from '~/src/App/config'
import safeStorage from '~/src/Lib/safeStorage'
import { hasNativeAuthTokens, removeNativeAuthTokens, writeNativeAuthTokens } from '~/src/Native/biometrics'
import { isNativePlatform } from '~/src/Native/platform'
import { readNativeSessionTokens, removeNativeSessionTokens, writeNativeSessionTokens } from '~/src/Native/session'
import { isNativeAccessTokenCurrent } from '~/src/Native/tokens'

import { PUBLIC_ROUTES } from '../Routes/constants'
import { isPublicRoute } from '../Routes/utils'
import getGlobal from './getGlobal'
import createLogger from './Logging'
import { EMPTY_ARRAY, EMPTY_OBJECT } from './Utils'

const localStorage = safeStorage('local')

const logger = createLogger('Lib/Auth')

const DEFAULT_TOKENS = {
  access: null,
  facilities: EMPTY_ARRAY,
  refresh: null,
  user: null,
}

let runtimeTokens = DEFAULT_TOKENS
let nativeSecureSession = false

export const AUTH_URL = '/token/'
export const AUTH_REFRESH_URL = `${config.API_URL}${AUTH_URL}refresh/`

export const getTokens = () => {
  if (isNativePlatform()) return runtimeTokens
  const storedTokens = localStorage.getItem(config.AUTH_TOKEN_KEY)
  if (storedTokens) {
    try {
      return JSON.parse(storedTokens)
    } catch {
      logger.error('Error parsing auth token JSON')
      localStorage.removeItem(config.AUTH_TOKEN_KEY)
    }
  }
  return DEFAULT_TOKENS
}

const deleteCookie = () => {
  const bareDomain = new URL(AUTH_REFRESH_URL)?.host?.match(/\w+\.\w+$/)?.[0]

  const cookie = ['aroya-auth=', `Domain=${bareDomain}`, 'Path=/', 'Max-Age=-999999'].join('; ')

  document.cookie = cookie
}

export const setTokens = payload => {
  if (isNativePlatform()) {
    runtimeTokens = payload ?? DEFAULT_TOKENS
    return payload
  }
  if (payload) {
    localStorage.setItem(config.AUTH_TOKEN_KEY, JSON.stringify(payload))
  }
  return payload
}

export const clearTokens = () => {
  if (isNativePlatform()) {
    runtimeTokens = DEFAULT_TOKENS
    nativeSecureSession = false
    deleteCookie()
    return
  }
  localStorage.removeItem(config.AUTH_TOKEN_KEY)
  deleteCookie()
}

export const clearLegacyTokens = () => localStorage.removeItem(config.AUTH_TOKEN_KEY)

export const setNativeBiometricSessionTokens = payload => {
  runtimeTokens = payload ?? DEFAULT_TOKENS
  nativeSecureSession = true
  return payload
}

export const persistNativeBiometricTokens = async payload => {
  await writeNativeAuthTokens(payload)
  return setNativeBiometricSessionTokens(payload)
}

export const restoreNativeSession = async () => {
  if (!isNativePlatform()) return false
  const tokens = await readNativeSessionTokens()
  if (!tokens) return false
  setTokens(tokens)
  return true
}

export const clearNativeSession = () => {
  runtimeTokens = DEFAULT_TOKENS
  nativeSecureSession = false
}

export const clearNativeSecureSession = () => {
  nativeSecureSession = false
}

export const clearNativeSolusOnlyMarker = async () => {
  if (!isNativePlatform()) return

  const tokens = getTokens()
  if (tokens?.solusOnly !== true) return

  const clearedTokens = { ...tokens }
  delete clearedTokens.solusOnly
  setTokens(clearedTokens)

  try {
    await writeNativeSessionTokens(clearedTokens)
  } catch (error) {
    logger.error('Unable to clear the native Solus-only session marker:', error)
  }

  try {
    if (isNativeSecureSession() || (await hasNativeAuthTokens())) await writeNativeAuthTokens(clearedTokens)
  } catch (error) {
    logger.error('Unable to clear the biometric Solus-only session marker:', error)
  }
}

export const isNativeSecureSession = () => nativeSecureSession

export const shouldRestoreTokensAtStartup = () => !isNativePlatform() || Boolean(getTokens().refresh)

export const shouldRefreshTokensAtStartup = () => !isNativePlatform() || !isNativeAccessTokenCurrent(getTokens())

// {"code":"token_not_valid"}
export const refreshTokens = async (store, options = EMPTY_OBJECT) => {
  const {
    // OAuth consent is a public route, but approving access is authenticated.
    // Callers that are already handling a 401 can opt into refresh without
    // letting the normal public-route guard strand them with a stale token.
    redirectOnFailure = true,
    skipPublicRoutes = true,
  } = options
  const oldTokens = getTokens()
  const { pathname } = getGlobal().location ?? EMPTY_OBJECT

  if (skipPublicRoutes && isPublicRoute(pathname)) {
    logger.debug('Skipping token refresh for public route:', pathname)
    return oldTokens
  }

  const response = await fetch(AUTH_REFRESH_URL, {
    body: JSON.stringify({ refresh: oldTokens.refresh }),
    cache: 'no-cache',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    mode: 'cors',
  })

  let updatedTokens
  let responseData
  let responseStatus
  try {
    responseData = await response.json()
    if (response.status !== 200) {
      responseStatus = response.status
      throw new Error(`Token refresh failed: ${responseStatus} ${JSON.stringify(responseData)}`)
    } else {
      updatedTokens = responseData
    }
  } catch (err) {
    logger.error(err.toString())
    clearTokens()
    if (isNativePlatform()) {
      await Promise.all([removeNativeAuthTokens(), removeNativeSessionTokens()]).catch(error =>
        logger.error('Unable to remove native auth tokens:', error),
      )
      clearNativeSession()
    }
    if (store) {
      store.doAuthReset()
      if (redirectOnFailure) {
        store.doUpdateUrl(PUBLIC_ROUTES.LOGIN)
      }
    } else if (redirectOnFailure) {
      location.replace(PUBLIC_ROUTES.LOGIN)
    }
    return false
  }

  if (updatedTokens !== DEFAULT_TOKENS) {
    const { access } = updatedTokens
    const currentMembership = (store ? store.selectCurrentMembership() : null) ?? EMPTY_OBJECT
    if (store && access?.length) {
      if (currentMembership.facility) {
        store.doConnectSSE(currentMembership)
      } else {
        console.warn('unable to connect sse due to missing currentMembership')
        if (!store.selectMeIsLoading()) {
          store.doMarkMeAsOutdated()
        }
      }
    } else {
      logger.warn('unable to refresh sse when refreshing token:', {
        access,
        currentMembership,
        store,
      })
    }
  } else {
    const extra = {
      pathname,
      reason: 'token-refresh-failed',
      responseData,
      status: response.status,
      statusText: response.statusText,
    }
    logger.warn('Invalid refresh token, redirecting to login page.', extra)
    if (config.SENTRY_DSN) {
      captureSentryEvent('login-redirect', { extra })
    }
    clearTokens()
    if (isNativePlatform()) {
      await Promise.all([removeNativeAuthTokens(), removeNativeSessionTokens()]).catch(error =>
        logger.error('Unable to remove native auth tokens:', error),
      )
      clearNativeSession()
    }
    if (store) {
      store.doAuthReset()
      if (redirectOnFailure) {
        store.doUpdateUrl(PUBLIC_ROUTES.LOGIN)
      }
    } else if (redirectOnFailure) {
      location.replace(PUBLIC_ROUTES.LOGIN)
    }
    return false
  }

  const tokens = { ...oldTokens, ...updatedTokens }
  const hasRefreshedFacilities = Array.isArray(updatedTokens?.facilities) && updatedTokens.facilities.length > 0
  const hasCurrentFacility = Boolean(store?.selectCurrentMembership?.()?.facility)
  if (hasRefreshedFacilities || hasCurrentFacility) delete tokens.solusOnly
  if (isNativePlatform()) {
    await writeNativeSessionTokens(tokens)
    if (isNativeSecureSession() || (await hasNativeAuthTokens())) await writeNativeAuthTokens(tokens)
  }
  return setTokens(tokens)
}
getGlobal().refreshTokens = refreshTokens
